import { Platform } from '@angular/cdk/platform';
import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatIconRegistry } from '@angular/material';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationCancel, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { NetPromoterScoreModalService } from '@core/components/net-promoter-score-modal/net-promoter-score-modal.service';
import { BlatoutService } from '@core/services/blatout/blatout.service';
import { PushNotificationsService } from '@core/services/push-notifications/push-notifications.service';
import { SocketService } from '@core/services/socket.service';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { FuseConfigService } from '@fuse/services/config.service';
import { TranslateService } from '@ngx-translate/core';
import { L10n, loadCldr } from '@syncfusion/ej2-base';
import * as caGregorian from 'cldr-data/main/pt/ca-gregorian.json';
import * as numbers from 'cldr-data/main/pt/numbers.json';
import * as timeZoneNames from 'cldr-data/main/pt/timeZoneNames.json';
import * as numberingSystems from 'cldr-data/supplemental/numberingSystems.json';
import { registerLocale } from 'i18n-iso-countries';
import { Subject } from 'rxjs';
import { delay, filter, first, takeUntil } from 'rxjs/operators';
import iconNames from './icons.json';

loadCldr(numberingSystems, caGregorian, numbers, timeZoneNames);

@Component({
  selector: 'app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  fuseConfig: any;
  navigation: any;

  private readonly subscriptions = new Subject<void>();

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly fuseConfigService: FuseConfigService,
    private readonly translateService: TranslateService,
    private readonly platform: Platform,
    private readonly router: Router,
    private readonly blatoutService: BlatoutService,
    private readonly socketService: SocketService,
    private readonly authenticationService: AuthenticationService,
    private readonly matDialog: MatDialog,
    private readonly pushNotificationsService: PushNotificationsService,
    private readonly fuseSidebarService: FuseSidebarService,
    private readonly matIconRegistry: MatIconRegistry,
    private readonly domSanitizer: DomSanitizer,
    private readonly netPromoterScoreModalService: NetPromoterScoreModalService
  ) {

    this.loadIcons();

    this.router.events
      .pipe(filter((event) => event instanceof NavigationStart || event instanceof NavigationEnd || event instanceof NavigationCancel))
      .subscribe((e) => {
        const event = e as NavigationStart;
      });

    const authenticatedNavigationStarted = this.router.events.pipe(
      takeUntil(this.subscriptions),
      filter((event) => event instanceof NavigationStart),
      filter((event: NavigationStart) => {
        const url = event.url;
        const isLogin = url === '/login';
        const isRecoverPassword = url.includes('/login/recuperar-senha');
        return !isLogin && !isRecoverPassword;
      })
    );

    authenticatedNavigationStarted.subscribe(() => {
      this.blatoutService.refresh();
    });

    const navigationStarted = this.router.events.pipe(
      takeUntil(this.subscriptions),
      filter((event) => event instanceof NavigationStart)
    );

    navigationStarted.subscribe(() => {
      this.matDialog.closeAll();
      this.fuseSidebarService.closeAll();
    });

    // Add languages
    this.translateService.addLangs(['es', 'en', 'pt']);
    registerLocale(require('i18n-iso-countries/langs/en.json'));
    registerLocale(require('i18n-iso-countries/langs/es.json'));
    registerLocale(require('i18n-iso-countries/langs/pt.json'));

    // Set the default language
    this.translateService.setDefaultLang('pt');

    // Use a language
    if (navigator.language.includes('en')) {
      this.translateService.use('en');
    }

    if (navigator.language.includes('pt')) {
      this.translateService.use('pt');
    }

    if (navigator.language.includes('es')) {
      this.translateService.use('es');
    }

    /**
     * ----------------------------------------------------------------------------------------------------
     * ngxTranslate Fix Start
     * ----------------------------------------------------------------------------------------------------
     */

    /**
     * If you are using a language other than the default one, i.e. Turkish in this case,
     * you may encounter an issue where some of the components are not actually being
     * translated when your app first initialized.
     *
     * This is related to ngxTranslate module and below there is a temporary fix while we
     * are moving the multi language implementation over to the Angular's core language
     * service.
     **/

    // Set the default language to 'en' and then back to 'tr'.
    // '.use' cannot be used here as ngxTranslate won't switch to a language that's already
    // been selected and there is no way to force it, so we overcome the issue by switching
    // the default language back and forth.
    /**
      setTimeout(() => {
        this._translateService.setDefaultLang('en');
        this._translateService.setDefaultLang('tr');
      });
    */

    /**
     * ----------------------------------------------------------------------------------------------------
     * ngxTranslate Fix End
     * ----------------------------------------------------------------------------------------------------
     */

    // Add is-mobile class to the body if the platform is mobile
    if (this.platform.ANDROID || this.platform.IOS) {
      this.document.body.classList.add('is-mobile');
    }

    // Fix mobile browser viewport height
    window.addEventListener('resize', () => {
      const vh = window.innerHeight * 0.01;
      this.document.documentElement.style.setProperty('--vh', `${vh}px`);
    });

    if (this.authenticationService.user) {
      this.socketService.reconnect();
      this.pushNotificationsService.refreshNotificationSubscription();
    }
  }

  ngOnInit(): void {
    // Subscribe to config changes
    this.fuseConfigService.config.pipe(takeUntil(this.subscriptions)).subscribe((config) => {
      this.fuseConfig = config;

      // Boxed
      if (this.fuseConfig.layout.width === 'boxed') {
        this.document.body.classList.add('boxed');
      } else {
        this.document.body.classList.remove('boxed');
      }

      // Color theme - Use normal for loop for IE11 compatibility
      for (let i = 0; i < this.document.body.classList.length; i++) {
        const className = this.document.body.classList[i];

        if (className.startsWith('theme-')) {
          this.document.body.classList.remove(className);
        }
      }

      this.document.body.classList.add(this.fuseConfig.colorTheme);
    });
    L10n.load({
      pt: {
        daterangepicker: {
          today: 'Hoje',
          startLabel: 'Começa em',
          endLabel: 'Termina em',
          applyText: 'Confirmar',
          cancelText: 'Cancelar',
          selectedDays: 'Dias selecionados',
          days: 'dias',
          customRange: 'Intervalo customizado'
        }
      }
    });

    this.showNpsDialogAfterFirstLoggedNavigationEnds();
  }

  showNpsDialogAfterFirstLoggedNavigationEnds(): void {
    const afterFistNavigation$ = this.router.events.pipe(
      takeUntil(this.subscriptions),
      filter((event) => event instanceof NavigationEnd),
      filter(() => this.authenticationService.permissions.length > 0),
      first(),
      delay(1000)
    );

    afterFistNavigation$.subscribe(() => {
      this.netPromoterScoreModalService.open();
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.next();
    this.subscriptions.complete();
  }

  loadIcons(): void {
    for (const iconName of iconNames) {
      this.matIconRegistry.addSvgIcon(iconName, this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/icon/${iconName}.svg`));
    }
  }
}
